import { IgnoreZone } from "../SiteAboutPageUtils/siteAboutInterfaces";
import { FlexPreset } from "./types";
import { FLEX_IZ_NO_IGNORE_ZONES_OPTION } from "./flexIzPresetSelectConstants";

const zoneHasDrawableCoordinates = (zone: IgnoreZone): boolean => {
  const walk = (node: unknown, depth: number): boolean => {
    if (depth > 6 || node == null) {
      return false;
    }
    if (Array.isArray(node)) {
      if (
        node.length >= 2 &&
        typeof node[0] === "number" &&
        typeof node[1] === "number" &&
        Number.isFinite(node[0]) &&
        Number.isFinite(node[1])
      ) {
        return true;
      }
      return node.some((child) => walk(child, depth + 1));
    }
    return false;
  };
  return walk(zone.coordinates as unknown, 0);
};

/**
 * Drop preset-zone rows whose label only echoes the preset title *and* carry no
 * drawable geometry (API bundle title rows). Keep same-named rows that have coordinates
 * — real zones often share the preset name after normalization.
 */
/** Rows from GET preset/?camera= (`server-{id}`) or unsaved drafts (`custom-`). */
export const isFlexPresetApiListOrDraft = (p: FlexPreset): boolean => {
  const id = p.localId || "";
  return id.startsWith("server-") || id.startsWith("custom-");
};

/**
 * Post-onboarding preset pickers (Edit Camera, Edit Ignore Zones, schedule editor, etc.):
 * only presets returned from the preset list API plus in-session drafts — not synthetic
 * `default-*` or `No Ignore Zones` rows. Initial Flex onboarding dialogs pass the full
 * store list instead of filtering through this helper.
 */
export const filterFlexPresetsForApiPresetDropdown = (presets: FlexPreset[]): FlexPreset[] =>
  presets.filter(
    (p) =>
      isFlexPresetApiListOrDraft(p) &&
      p.localId !== FLEX_IZ_NO_IGNORE_ZONES_OPTION
  );

/**
 * When schedule/UI still references `default-{cameraId}`, map to the matching `server-{id}`
 * row using the canonical default slot’s `persistedId`. Leaves `__no_ignore_zones__` unchanged.
 */
export const normalizeFlexPresetSelectionToApiBackedMenuValue = (
  cameraId: number,
  allPresets: FlexPreset[],
  selectedLocalId: string
): string => {
  if (!selectedLocalId || selectedLocalId === FLEX_IZ_NO_IGNORE_ZONES_OPTION) {
    return selectedLocalId;
  }
  const defaultLid = `default-${cameraId}`;
  if (selectedLocalId !== defaultLid) {
    return selectedLocalId;
  }
  const slot = allPresets.find((p) => p.localId === defaultLid);
  if (slot?.persistedId == null || !Number.isFinite(Number(slot.persistedId))) {
    return selectedLocalId;
  }
  const pid = Number(slot.persistedId);
  const server = allPresets.find(
    (p) =>
      typeof p.localId === "string" &&
      p.localId.startsWith("server-") &&
      p.persistedId != null &&
      Number(p.persistedId) === pid
  );
  return server?.localId ?? selectedLocalId;
};

export const filterBundleTitleZones = (presetName: string, zones: IgnoreZone[]): IgnoreZone[] => {
  const pn = presetName.trim().toLowerCase();
  if (!pn) {
    return zones;
  }
  return zones.filter((zone) => {
    const dn = (zone.label_display_name || "").trim().toLowerCase();
    if (dn !== pn) {
      return true;
    }
    return zoneHasDrawableCoordinates(zone);
  });
};

/** Collapse duplicate preset menu labels; prefer more zones, then earlier in list. */
export const dedupeFlexPresetsByDisplayName = (cameraId: number, presets: FlexPreset[]): FlexPreset[] => {
  const defaultLocalId = `default-${cameraId}`;
  const defaultEntry = presets.find((p) => p.localId === defaultLocalId);
  const noIgnoreEntry = presets.find((p) => p.localId === FLEX_IZ_NO_IGNORE_ZONES_OPTION);
  /** Keep API `server-*` rows even when they share `persistedId` with the synthetic default; dropdowns filter `default-*` out and need the real preset row. */
  const rest = presets.filter(
    (p) => p.localId !== defaultLocalId && p.localId !== FLEX_IZ_NO_IGNORE_ZONES_OPTION
  );
  const indexByLocalId = new Map(rest.map((p, i) => [p.localId, i]));
  const nameBuckets = new Map<string, FlexPreset[]>();
  for (const p of rest) {
    const key = (p.name || "").trim().toLowerCase();
    const bucketKey = key.length > 0 ? key : `__id:${p.localId}`;
    const arr = nameBuckets.get(bucketKey) || [];
    arr.push(p);
    nameBuckets.set(bucketKey, arr);
  }
  const picked: FlexPreset[] = [];
  for (const group of nameBuckets.values()) {
    if (group.length === 1) {
      picked.push(group[0]);
      continue;
    }
    const best = group.reduce((a, b) => {
      const za = a.zones?.length ?? 0;
      const zb = b.zones?.length ?? 0;
      if (zb !== za) {
        return zb > za ? b : a;
      }
      const ia = indexByLocalId.get(a.localId) ?? 0;
      const ib = indexByLocalId.get(b.localId) ?? 0;
      return ia <= ib ? a : b;
    });
    picked.push(best);
  }
  picked.sort((a, b) => (indexByLocalId.get(a.localId) ?? 0) - (indexByLocalId.get(b.localId) ?? 0));
  return [
    ...(defaultEntry ? [defaultEntry] : []),
    ...picked,
    ...(noIgnoreEntry ? [noIgnoreEntry] : []),
  ];
};

export const isNoIgnorePreset = (preset: FlexPreset): boolean =>
  preset.localId === FLEX_IZ_NO_IGNORE_ZONES_OPTION ||
  (preset.name || "").trim().toLowerCase() === "no ignore zones";

/**
 * For dropdown display only: collapse duplicate "No Ignore Zones" entries
 * (synthetic + persisted) to a single option. Prefer the currently selected id,
 * otherwise prefer a persisted non-synthetic option.
 */
export const collapseNoIgnoreZonesPresetOptions = (
  presets: FlexPreset[],
  preferredLocalId?: string
): FlexPreset[] => {
  const noIgnoreOptions = presets.filter(isNoIgnorePreset);
  if (noIgnoreOptions.length <= 1) {
    return presets;
  }
  const preferred = preferredLocalId
    ? noIgnoreOptions.find((preset) => preset.localId === preferredLocalId)
    : undefined;
  const persisted = noIgnoreOptions.find(
    (preset) =>
      preset.persistedId != null && preset.localId !== FLEX_IZ_NO_IGNORE_ZONES_OPTION
  );
  const keep = preferred || persisted || noIgnoreOptions[0];
  return presets.filter(
    (preset) => !isNoIgnorePreset(preset) || preset.localId === keep.localId
  );
};
